import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Zap, ChevronDown, Plus, Filter, CheckCircle, Menu } from 'lucide-react';
import { getAccountsWithRules } from 'services/accounts/accountService';
import AccountDefaultImage from 'assets/images/avatars/default-avatar.jpg';
import EmptyAccountsState from 'components/accounts/EmptyAccountsState';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import { AppDispatch, RootState } from 'config/redux/store';
import { setSelectedUser } from 'config/redux/slices/userSlice';

interface Account {
  _id: string;
  username: string;
  name: string;
  profile_picture_base64?: string;
}

interface RuleCriterion {
  type: string;
  filter?: string;
  id?: string;
  [key: string]: unknown;
}

interface Rule {
  _id: string;
  rule_name: string;
  rule: RuleCriterion[];
  status: string;
  is_active: boolean;
  actions: number;
}

interface AccountApi {
  _id: string;
  username?: string;
  name?: string;
  profile_picture_base64?: string;
}

const Rules: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const selectedUser = useSelector((state: RootState) => state.user.selectedUser);
  const [hasAccounts, setHasAccounts] = useState<boolean | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [rules, setRules] = useState<Record<string, Rule[]>>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inDesktop = dropdownRef.current?.contains(target);
      const inMobile = mobileDropdownRef.current?.contains(target);
      if (!inDesktop && !inMobile) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch accounts and rules in a single API call
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAccountsWithRules();
        const accountsList = Array.isArray(response.data?.accounts) ? response.data.accounts : [];
        if (accountsList.length === 0) {
          setHasAccounts(false);
          setLoading(false);
          return;
        }
        setHasAccounts(true);

        const transformedAccounts: Account[] = accountsList.map((acc: AccountApi) => ({
          _id: acc._id,
          username: acc.username || 'unknown',
          name: acc.name || acc.username || 'Unknown',
          profile_picture_base64: acc.profile_picture_base64 || '',
        }));
        setAccounts(transformedAccounts);
        const preferred = (selectedUser && transformedAccounts.find(a => a._id === selectedUser)) || transformedAccounts[0];
        setSelectedAccount(preferred);
        dispatch(setSelectedUser(preferred._id));

        if (response.data?.rules) {
          setRules(response.data.rules);
        }
      } catch {
        setHasAccounts(false);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <Spinner size="large" text={t('common.loading')} fullPage />;
  }

  if (!hasAccounts) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <EmptyAccountsState onManageAccount={() => navigate('/accounts')} />
      </div>
    );
  }

  const currentRules = selectedAccount ? (rules[selectedAccount._id] || []) : [];
  const totalRules = currentRules.length;
  const totalCriteria = currentRules.reduce((sum, r) => sum + (Array.isArray(r.rule) ? r.rule.length : 0), 0);
  const activeRules = currentRules.filter(r => r.is_active || r.status?.toLowerCase() === 'active').length;

  const handleAccountSelect = (account: Account) => {
    setSelectedAccount(account);
    setIsDropdownOpen(false);
    dispatch(setSelectedUser(account._id));
  };

  const getStatusTag = (status: string) => {
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_') || 'active';
    const styles: Record<string, { bg: string; border: string; color: string; label: string }> = {
      active: {
        bg: isDark ? '#022C22' : '#ECFDF5',
        border: isDark ? '#009966' : '#5EE9B5',
        color: isDark ? '#5EE9B5' : '#009966',
        label: t('rules.status.active', 'Active'),
      },
      paused: {
        bg: isDark ? '#461901' : '#FFFBEB',
        border: isDark ? '#E17100' : '#FFD230',
        color: isDark ? '#FFD230' : '#E17100',
        label: t('rules.status.paused', 'Paused'),
      },
      ready_to_activate: {
        bg: isDark ? '#162456' : '#EFF6FF',
        border: isDark ? '#155DFC' : '#8EC5FF',
        color: isDark ? '#8EC5FF' : '#155DFC',
        label: t('rules.status.readyToActivate', 'Ready to activate'),
      },
    };

    const s = styles[normalizedStatus] || styles.active;
    const tagPadding = normalizedStatus === 'active' ? '4px 8px' : '2px 6px';
    return (
      <span
        style={{
          fontSize: '12px',
          lineHeight: '16px',
          fontWeight: 500,
          textAlign: 'center' as const,
          background: s.bg,
          border: `1px solid ${s.border}`,
          color: s.color,
          borderRadius: '999px',
          padding: tagPadding,
          whiteSpace: 'nowrap',
        }}
      >
        {s.label}
      </span>
    );
  };

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark
    ? '0px 1px 2px rgba(2, 6, 24, 0.5)'
    : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const selectorBg = isDark ? '#0F172B' : '#FFFFFF';
  const selectorBorder = isDark ? '#314158' : '#E2E8F0';
  const overviewCardBorder = isDark ? '#1D293D' : '#F1F5F9';
  const ruleCardBorder = isDark ? '#314158' : '#E2E8F0';
  const ruleCardShadow = isDark
    ? '0px 1px 2px rgba(2, 6, 24, 0.3)'
    : '0px 1px 2px rgba(43, 127, 255, 0.06)';

  // Overview card icon configs
  const overviewCards = [
    {
      icon: <Zap size={24} />,
      iconBg: isDark ? '#162456' : '#EFF6FF',
      iconColor: isDark ? '#51A2FF' : '#2B7FFF',
      count: totalRules,
      label: t('rules.overview.rules', 'Rules'),
    },
    {
      icon: <Filter size={24} />,
      iconBg: isDark ? '#3C0366' : '#FAF5FF',
      iconColor: isDark ? '#C27AFF' : '#AD46FF',
      count: totalCriteria,
      label: t('rules.overview.criteria', 'Criteria'),
    },
    {
      icon: <CheckCircle size={24} />,
      iconBg: isDark ? '#022C22' : '#ECFDF5',
      iconColor: isDark ? '#00D492' : '#00BC7D',
      count: activeRules,
      label: t('rules.overview.active', 'Active'),
    },
  ];

  return (
    <div
      className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        gap: '8px',
        background: containerBg,
      }}
    >
      {/* Main content wrapper */}
      <div
        className="flex flex-col flex-1 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{
          background: mainBg,
          boxShadow: mainShadow,
        }}
      >
        {/* Header */}
        <div
          className="flex flex-col justify-center items-start w-full flex-shrink-0"
          style={{
            padding: '12px 16px',
            gap: '8px',
            borderBottom: `1px solid ${headerBorder}`,
            background: mainBg,
          }}
        >
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
          {/* Left: Hamburger (mobile) + Zap icon + Title */}
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div
              className="hidden lg:flex items-center justify-center flex-shrink-0"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '12px',
                background: isDark ? '#162456' : '#EFF6FF',
              }}
            >
              <Zap size={24} color={isDark ? '#51A2FF' : '#2B7FFF'} />
            </div>
            <h1
              className="font-bold flex-1"
              style={{ fontSize: '20px', lineHeight: '28px', color: titleColor, margin: 0 }}
            >
              {t('navigation.rules', 'Rules')}
            </h1>

          {/* Right: Account selector (desktop) + New Automation button */}
            {/* Account Selector Dropdown — desktop only */}
            <div className="relative hidden sm:block" ref={dropdownRef}>
              <button
                className="flex items-center transition-all"
                style={{
                  padding: '8px 12px',
                  gap: '4px',
                  background: selectorBg,
                  border: `1px solid ${selectorBorder}`,
                  borderRadius: '8px',
                  height: '36px',
                  cursor: accounts.length > 1 ? 'pointer' : 'default',
                }}
                onClick={() => accounts.length > 1 && setIsDropdownOpen(!isDropdownOpen)}
              >
                {selectedAccount && (
                  <>
                    <img
                      src={selectedAccount.profile_picture_base64 || AccountDefaultImage}
                      alt={selectedAccount.username}
                      className="flex-shrink-0 object-cover"
                      style={{ width: '20px', height: '20px', borderRadius: '12px' }}
                      onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                    />
                    <span
                      style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor }}
                    >
                      {selectedAccount.username}
                    </span>
                  </>
                )}
                {accounts.length > 1 && (
                  <ChevronDown
                    size={20}
                    color={isDark ? '#CAD5E2' : '#45556C'}
                    className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              {isDropdownOpen && accounts.length > 1 && (
                <div
                  className="absolute right-0 mt-2 z-50 max-h-60 overflow-y-auto"
                  style={{
                    width: '220px',
                    background: mainBg,
                    border: `1px solid ${selectorBorder}`,
                    borderRadius: '8px',
                    boxShadow: isDark
                      ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5)'
                      : '0px 10px 15px -3px rgba(43, 127, 255, 0.1)',
                  }}
                >
                  {accounts.map((account) => (
                    <div
                      key={account._id}
                      className="flex items-center gap-2 cursor-pointer transition-all"
                      style={{
                        padding: '8px 12px',
                        background: selectedAccount?._id === account._id
                          ? (isDark ? '#162456' : '#EFF6FF')
                          : 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedAccount?._id !== account._id) {
                          e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedAccount?._id !== account._id) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                      onClick={() => handleAccountSelect(account)}
                    >
                      <img
                        src={account.profile_picture_base64 || AccountDefaultImage}
                        alt={account.username}
                        className="flex-shrink-0 object-cover"
                        style={{ width: '24px', height: '24px', borderRadius: '10px' }}
                        onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                      />
                      <span
                        className="flex-1 text-sm font-medium truncate"
                        style={{ color: titleColor }}
                      >
                        {account.username}
                      </span>
                      {selectedAccount?._id === account._id && (
                        <CheckCircle size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* New Automation Button */}
            <button
              className="flex items-center justify-center transition-opacity hover:opacity-90"
              style={{
                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                borderRadius: '8px',
                padding: '8px 12px',
                gap: '4px',
                height: '36px',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/rules/new', { state: { selectedAccount, accounts } })}
            >
              <Plus size={20} color="#FFFFFF" />
              <span className="hidden sm:inline" style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: '#FFFFFF' }}>
                {t('rules.newAutomation', 'New Automation')}
              </span>
              <span className="sm:hidden" style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: '#FFFFFF' }}>
                New
              </span>
            </button>
          </div>

          {/* Row 2 (mobile only): Account dropdown */}
          <div className="sm:hidden relative w-full" ref={mobileDropdownRef}>
            <button
              className="flex items-center transition-all w-full"
              style={{
                padding: '8px 12px',
                gap: '4px',
                background: selectorBg,
                border: `1px solid ${selectorBorder}`,
                borderRadius: '8px',
                height: '36px',
                cursor: accounts.length > 1 ? 'pointer' : 'default',
              }}
              onClick={() => accounts.length > 1 && setIsDropdownOpen(!isDropdownOpen)}
            >
              {selectedAccount && (
                <>
                  <img
                    src={selectedAccount.profile_picture_base64 || AccountDefaultImage}
                    alt={selectedAccount.username}
                    className="flex-shrink-0 object-cover"
                    style={{ width: '20px', height: '20px', borderRadius: '12px' }}
                    onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                  />
                  <span
                    style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 500, color: titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textAlign: 'left' }}
                  >
                    {selectedAccount.username}
                  </span>
                </>
              )}
              {accounts.length > 1 && (
                <ChevronDown
                  size={20}
                  color={isDark ? '#CAD5E2' : '#45556C'}
                  className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              )}
            </button>

            {isDropdownOpen && accounts.length > 1 && (
              <div
                className="absolute left-0 mt-2 z-50 max-h-60 overflow-y-auto w-full"
                style={{
                  background: mainBg,
                  border: `1px solid ${selectorBorder}`,
                  borderRadius: '8px',
                  boxShadow: isDark
                    ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5)'
                    : '0px 10px 15px -3px rgba(43, 127, 255, 0.1)',
                }}
              >
                {accounts.map((account) => (
                  <div
                    key={account._id}
                    className="flex items-center gap-2 cursor-pointer transition-all"
                    style={{
                      padding: '8px 12px',
                      background: selectedAccount?._id === account._id
                        ? (isDark ? '#162456' : '#EFF6FF')
                        : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedAccount?._id !== account._id) {
                        e.currentTarget.style.background = isDark ? '#1D293D' : '#F8FAFC';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedAccount?._id !== account._id) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                    onClick={() => handleAccountSelect(account)}
                  >
                    <img
                      src={account.profile_picture_base64 || AccountDefaultImage}
                      alt={account.username}
                      className="flex-shrink-0 object-cover"
                      style={{ width: '24px', height: '24px', borderRadius: '10px' }}
                      onError={(e) => { (e.target as HTMLImageElement).src = AccountDefaultImage; }}
                    />
                    <span
                      className="flex-1 text-sm font-medium truncate"
                      style={{ color: titleColor }}
                    >
                      {account.username}
                    </span>
                    {selectedAccount?._id === account._id && (
                      <CheckCircle size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto" style={{ padding: '16px' }}>
          {/* Overview Section */}
          <div className="flex flex-col gap-4">
            <h2
              style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}
            >
              {t('rules.sections.overview', 'Overview')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: '8px' }}>
              {overviewCards.map((card, index) => (
                <div
                  key={index}
                  className="flex flex-col"
                  style={{
                    padding: '16px',
                    gap: '8px',
                    background: mainBg,
                    border: `1px solid ${overviewCardBorder}`,
                    borderRadius: '16px',
                  }}
                >
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '12px',
                      background: card.iconBg,
                      color: card.iconColor,
                    }}
                  >
                    {card.icon}
                  </div>
                  <div className="flex flex-col" style={{ gap: '2px' }}>
                    <span style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor }}>
                      {card.count}
                    </span>
                    <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 400, color: subtitleColor }}>
                      {card.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rules Section */}
          <div className="flex flex-col gap-4">
            <h2
              style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 700, color: titleColor }}
            >
              {t('rules.sections.rules', 'Rules')}
            </h2>

            {currentRules.length === 0 ? (
              <div
                className="flex items-center justify-center"
                style={{
                  padding: '48px 24px',
                  border: `1px solid ${overviewCardBorder}`,
                  borderRadius: '16px',
                }}
              >
                <p
                  className="text-sm"
                  style={{ color: subtitleColor }}
                >
                  {t('rules.noRules', 'No rules created yet. Click "New Automation" to get started.')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" style={{ gap: '16px' }}>
                {currentRules.map((rule) => (
                  <div
                    key={rule._id}
                    className="flex flex-col transition-opacity hover:opacity-80"
                    style={{
                      padding: '16px',
                      gap: '8px',
                      background: mainBg,
                      border: `1px solid ${ruleCardBorder}`,
                      borderRadius: '8px',
                      boxShadow: ruleCardShadow,
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/rules/${rule._id}`, { state: { rule, selectedAccount, accounts } })}
                  >
                    <div className="flex items-start" style={{ gap: '8px' }}>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 500, color: titleColor }}
                          className="truncate"
                        >
                          {rule.rule_name}
                        </span>
                        <span style={{ fontSize: '16px', lineHeight: '24px', fontWeight: 400, color: subtitleColor }}>
                          {Array.isArray(rule.rule) ? rule.rule.length : 0} {t('rules.criteriaLabel', 'criteria')}
                        </span>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusTag(rule.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rules;
